/**
 * host 模式(宿主 site.xbwhynot.com)的纯函数:模式判定、token 解析、节点角色、移植节点树。
 * 契约:附录 A.45(角色)/ A.47(transplant 形状)/ A.53(图片 url)/ A.57(console 判定 host 模式)。
 * 中文词表(页 / 块 / 字段)在 `ui/components/host/hostLabels.ts`,这里不放人话。
 */
import type {
  NodeRole,
  UploadImageRequest,
  PublisherMode,
  SiteContent,
  SiteTransplant,
  TransplantNode,
} from "../api/types";

/** A.45 判定表里的 19 个角色(顺序 = 判定顺序)。 */
export const NODE_ROLES: readonly NodeRole[] = [
  "color",
  "dynamic",
  "nav",
  "link",
  "img-logo",
  "img-hero",
  "img-item",
  "alt",
  "slot",
  "fact-company",
  "fact-address",
  "fact-phone",
  "fact-email",
  "fact-icp",
  "copyright",
  "ui",
  "empty",
  "para",
  "heading",
];

/** `transplant.nodes` 允许的角色(A.47);编辑页只画这些。 */
export const TRANSPLANT_ROLES: readonly NodeRole[] = [
  "fact-company",
  "fact-address",
  "fact-phone",
  "fact-email",
  "fact-icp",
  "copyright",
  "heading",
  "para",
  "slot",
  "alt",
];

export function isNodeRole(x: unknown): x is NodeRole {
  return typeof x === "string" && (NODE_ROLES as readonly string[]).includes(x);
}

export function isTransplantRole(x: unknown): x is NodeRole {
  return typeof x === "string" && (TRANSPLANT_ROLES as readonly string[]).includes(x);
}

/** 事实节点(A.55 / A.56):api 按采集事实直填,改值 / AI 重写都 403。页面上锁,改事实回采集页。 */
export function isFactRole(role: string): boolean {
  return role.startsWith("fact-");
}

/** 节点值上限(A.56:PUT content 每节点 ≤ 500 字)。 */
export const HOST_NODE_MAX = 500;

/** 用户在编辑页改过的节点 / 图片绑定(A.30 / A.56)。 */
export const USER_EDIT = "user:edit";

/**
 * 这是不是 host 模式的站(A.57:`SiteDetail.publisherMode == host`)。
 * ★ 兜底认 `content.transplant`:SiteCard.publisherMode 的老口径是「当前线上版本的网关、没发布过为 null」——
 *   从没发布过的 host 站如果 api 还按老口径回 null,编辑页会被误画成 stub 五屏;有移植稿就一定是 host。
 */
export function isHostSite(
  site: { publisherMode: PublisherMode | null } | null | undefined,
  content?: Pick<SiteContent, "transplant"> | null
): boolean {
  return site?.publisherMode === "host" || content?.transplant != null;
}

export interface ParsedToken {
  page: string;
  block: string;
  /** block 之后的剩余路径(`siteName.content` / `items.0.title` / `content`)。 */
  field: string;
}

/** `{{site.<page>.<block>.<field…>}}` → 三段;认不出回 null(A.45 / 宿主字段模型)。 */
export function parseToken(token: string): ParsedToken | null {
  const inner = token.startsWith("{{") && token.endsWith("}}") ? token.slice(2, -2) : token;
  const p = inner.split(".");
  if (p[0] !== "site" || p.length < 4) return null;
  return { page: p[1], block: p[2], field: p.slice(3).join(".") };
}

export interface HostNodeRow {
  token: string;
  field: string;
  node: TransplantNode;
}

export interface HostBlockGroup {
  block: string;
  nodes: HostNodeRow[];
}

export interface HostPageGroup {
  page: string;
  blocks: HostBlockGroup[];
}

/**
 * 页的兜底顺序(模板详情没给 `profileSummary.pages` 时用):首页 → 介绍 → 产品 / 服务 / 案例 → 资讯 → 联系。
 * 不在表里的页排在表里的之后(保持出现顺序),token 解析不了的「其他」页(`""`)永远最后。
 */
const PAGE_FALLBACK_ORDER = [
  "home",
  "about",
  "aboutus",
  "brand",
  "brandstory",
  "culture",
  "product",
  "products",
  "service",
  "services",
  "solution",
  "case",
  "cases",
  "honor",
  "news",
  "articles",
  "article",
  "careers",
  "contact",
];

/** 块的「位置档」:导航 → 首屏 → 正文 → 页脚。 */
function blockBand(base: string): number {
  const b = base.toLowerCase();
  if (b === "navbar" || b === "header" || b === "nav" || b === "menu") return 0;
  if (b === "herosection" || b === "hero" || b === "banner") return 1;
  if (b === "footer") return 3;
  return 2;
}

function isImageBlock(base: string): boolean {
  return /^(image|images|carousel|gallery|picture)$/i.test(base);
}

/** 同档同序号时的先后:文字块 → 其它(卡片 / 表单…)→ 图片块(宿主模板里配图通常跟在它的文字后面)。 */
function bodyKind(base: string): number {
  if (/^(text|title|heading)$/i.test(base)) return 0;
  return isImageBlock(base) ? 2 : 1;
}

/** 块键 → (基名, 尾部序号);没有序号的记 0。 */
function splitBlock(block: string): { base: string; n: number } {
  const m = /^(.*?)(\d+)$/.exec(block);
  return m ? { base: m[1], n: Number(m[2]) } : { base: block, n: 0 };
}

/**
 * 字段在块内的先后:先按字段里的数组下标(`items.2.title` → 2),再按字段名常见顺序(标题在描述前、说明文字最后),
 * 都一样就保持原顺序(稳定排序)。
 */
const FIELD_RANK = [
  "siteName",
  "companyName",
  "title",
  "heading",
  "name",
  "subtitle",
  "description",
  "desc",
  "content",
  "text",
  "label",
  "caption",
  "button",
  "alt",
];

function fieldKey(field: string): { idx: number[]; rank: number } {
  const idx = (field.match(/\d+/g) ?? []).map(Number);
  const last = field
    .replace(/\[\d+\]|\.\d+/g, "")
    .split(".")
    .filter(Boolean);
  const names = last.length ? last : [field];
  let rank = FIELD_RANK.length;
  for (const n of names) {
    const r = FIELD_RANK.indexOf(n);
    if (r >= 0) {
      rank = r;
      break;
    }
  }
  return { idx, rank };
}

function cmp(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

function compareIdx(a: number[], b: number[]): number {
  for (let i = 0; i < Math.max(a.length, b.length); i += 1) {
    const d = (a[i] ?? -1) - (b[i] ?? -1);
    if (d !== 0) return d;
  }
  return 0;
}

/**
 * 移植节点 → 页 → 块 → 节点,**自己排序,不信键序**。
 * ★ 0924 E2E:`transplant.nodes` 存在 MySQL JSON 列里,读回来的键被按「长度 → 字典序」重排
 *   (`{{site.home.text7.content}}` 排到 `{{site.home.navbar0.siteName.content}}` 前面),编辑页节点树
 *   就把导航栏 / 首屏排到了文本区后面。所以这里按:页(`pageOrder` = 模板画像的页顺序,没给就用兜底表)
 *   → 块(导航 → 首屏 → 正文按块序号 → 页脚;同序号文字 → 卡片等 → 图片)→ 字段(数组下标 → 字段名常见顺序)排序,
 *   每一级最后按键本身兜底,同一份节点不管键序怎么来,排出来都一样。
 *   只收 A.47 那 10 种角色;token 解析不了的归到「其他」页(`page = ""`),排最后,不丢。
 */
export function buildNodeTree(
  transplant: SiteTransplant | null | undefined,
  pageOrder?: readonly string[] | null
): HostPageGroup[] {
  const pages: HostPageGroup[] = [];
  if (!transplant) return pages;
  for (const [token, node] of Object.entries(transplant.nodes ?? {})) {
    if (!node || !isTransplantRole(node.role)) continue;
    const parsed = parseToken(token);
    const pageKey = parsed?.page ?? "";
    const blockKey = parsed?.block ?? "";
    let page = pages.find((p) => p.page === pageKey);
    if (!page) {
      page = { page: pageKey, blocks: [] };
      pages.push(page);
    }
    let block = page.blocks.find((b) => b.block === blockKey);
    if (!block) {
      block = { block: blockKey, nodes: [] };
      page.blocks.push(block);
    }
    block.nodes.push({ token, field: parsed?.field ?? token, node });
  }
  const order = pageOrder && pageOrder.length > 0 ? pageOrder : PAGE_FALLBACK_ORDER;
  const pageRank = (p: string) => {
    if (p === "") return Number.MAX_SAFE_INTEGER;
    const i = order.indexOf(p);
    return i >= 0 ? i : order.length;
  };
  // 每一级最后都用键本身兜底比较:同一份节点无论键序怎么来,排出来都一样
  pages.sort((a, b) => pageRank(a.page) - pageRank(b.page) || cmp(a.page, b.page));
  for (const page of pages) {
    page.blocks.sort((a, b) => {
      const x = splitBlock(a.block);
      const y = splitBlock(b.block);
      return (
        blockBand(x.base) - blockBand(y.base) ||
        x.n - y.n ||
        bodyKind(x.base) - bodyKind(y.base) ||
        cmp(a.block, b.block)
      );
    });
    for (const block of page.blocks) {
      block.nodes.sort((a, b) => {
        const x = fieldKey(a.field);
        const y = fieldKey(b.field);
        return compareIdx(x.idx, y.idx) || x.rank - y.rank || cmp(a.field, b.field);
      });
    }
  }
  return pages;
}

/** 独立图片块(`image0` / `images1` / `carousel0`,不是卡片里的 items 槽位)。 */
export function isImageBlockKey(block: string): boolean {
  return isImageBlock(splitBlock(block).base);
}

/** 改一个节点的值,binding 置 `user:edit`(A.56);节点不存在原样返回。纯函数。 */
export function setNodeValue(content: SiteContent, token: string, value: string): SiteContent {
  const tp = content.transplant;
  const node = tp?.nodes[token];
  if (!tp || !node) return content;
  return {
    ...content,
    transplant: { ...tp, nodes: { ...tp.nodes, [token]: { ...node, value, binding: USER_EDIT } } },
  };
}

/** 宿主站的根(A.42 `app.website.host.base-url` 默认值;上传回来的相对 url 补它,A.53)。 */
export const HOST_ORIGIN = "https://site.xbwhynot.com";

/** 宿主相对 url(`/uploads/x.png`)→ 完整 url;已是 http(s) 的原样。 */
export function absoluteHostUrl(url: string): string {
  const v = url.trim();
  if (/^https?:\/\//i.test(v)) return v;
  return `${HOST_ORIGIN}${v.startsWith("/") ? "" : "/"}${v}`;
}

/** 宿主站自己的图(`/uploads/…` 相对路径或宿主完整 url)直连;别的外链要走图片代理(A.53)。 */
export function isHostImage(value: string): boolean {
  const v = value.trim();
  return !/^https?:\/\//i.test(v) || v.startsWith(HOST_ORIGIN);
}

export interface ImageSlot {
  /** site.json 路径:`brand.logo` / `brand.heroImage` / `products[i].image`(A.51 / A.53)。 */
  path: string;
  label: string;
  purpose: UploadImageRequest["purpose"];
  productIndex?: number;
  aspect: number | null;
}

/** logo / 首页首图 / 每个产品一格(产品名做标签)。 */
export function hostImageSlots(content: SiteContent): ImageSlot[] {
  const slots: ImageSlot[] = [
    { path: "brand.logo", label: "Logo", purpose: "logo", aspect: 1 },
    { path: "brand.heroImage", label: "首页首图", purpose: "hero", aspect: 16 / 9 },
  ];
  content.products.forEach((p, i) => {
    const name = typeof p?.name === "string" && p.name.trim() ? p.name : `产品 ${i + 1}`;
    slots.push({
      path: `products[${i}].image`,
      label: `产品图 · ${name}`,
      purpose: "product",
      productIndex: i,
      aspect: null,
    });
  });
  return slots;
}
